import styled from '@emotion/styled';
import { Button } from 'antd';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import dayjs from 'lib/dayjs';
import { useEffect, ReactNode, useState, useRef, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { Image } from './FeedbackTable';
import SiderImageSlide from 'views/Conversation/Sider/SiderImageSlide';
import { PreviewModalProvider } from 'views/Conversation/Sider/PreviewModal';
import { useGetAllSticker } from 'hooks/business/im/sticker';
import { useMount } from 'ahooks';
import { OperateLogSticker } from './OperateLogSticker';

const Content = styled.div`
  position: relative;
  padding-left: 16px;
  border-left: dashed 1px var(--fett-border-strong);
  color: var(--fett-text-tertiary);
  &::before,
  &::after {
    content: '';
    display: block;
    position: absolute;
    left: -1px;
    width: 1px;
    height: 11px;
    background: var(--fett-bg-panel);
  }
  &::before {
    top: 0;
  }
  &::after {
    bottom: 0;
  }
  > * {
    margin-bottom: 12px;
  }
`;

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  line-height: 22px;
  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 6px;
    left: -21px;
    width: 9px;
    height: 9px;
    border-radius: 5px;
    background: #d3d7e0;
    z-index: 2;
  }
  > p {
    flex: 1 0 0;
    margin-bottom: 0;
    color: var(--fett-text-secondary);
  }
  > span {
    flex: 0 0 auto;
    margin-left: 6px;
    font-size: 12px;
  }
`;

type TextMap = Util.PartialRequired<Partial<Record<General.SystemLanguage, string[]>>, 'zh-CN'>;

const getDefaultTextMap = (logList: Feedback.CaseOperateLog[]): TextMap => {
  const cnLogList = logList.reduce<string[]>((res, log) => {
    const { operateLog } = log;

    try {
      const contentList: Feedback.CaseOperateLogContent[] = JSON.parse(operateLog);

      return contentList.reduce((res, logContent) => {
        const { type, content } = logContent;

        if (type === 'text' && content) {
          res.push(content);
        }

        return res;
      }, res);
    } catch (err) {
      return res;
    }
  }, []);

  return {
    'zh-CN': cnLogList,
  };
};

export interface OperateLogProps {
  logList: Feedback.CaseOperateLog[];
  message: string;
  render?: (content: ReactNode, translateButton: ReactNode) => JSX.Element;
}

function OperateLog(props: OperateLogProps) {
  const { logList, render, message } = props;

  const { t } = useTranslate();

  const [shouldTranslate, setShouldTranslate] = useState(true);

  const [translating, setTranslating] = useState(false);

  const [translateFailed, setTranslateFailed] = useState(false);

  const [textMap, setTextMap] = useState<TextMap>(() => getDefaultTextMap(logList));

  useEffect(() => {
    setTextMap(getDefaultTextMap(logList));
  }, [logList]);

  const systemLanguage = useSystemLanguageValue();

  const {
    'zh-CN': cnTextList,
    [shouldTranslate ? systemLanguage : 'zh-CN']: currentLanguageTextList,
  } = textMap;

  const displayTextList = currentLanguageTextList || cnTextList;

  let textIndex = 0;

  const contentList = logList.map((log) => {
    const { createTime, operateLog } = log;

    let logContent: Feedback.CaseOperateLogContent[];

    try {
      logContent = JSON.parse(operateLog);

      const [first, ...rest] = logContent;

      const contentRender = (logContent: Feedback.CaseOperateLogContent) => {
        const { type, content } = logContent;

        if (type === 'text') {
          if (content) {
            const text = displayTextList[textIndex];

            textIndex += 1;

            return <p>{text}</p>;
          }

          return null;
        }

        // 自动复验的展示
        if (type === 'dialogue') {
          const dialogueContent = JSON.parse(content);

          return <DialogueReviewedView source={dialogueContent} message={message} />;
        }

        return {
          image: (
            <div>
              <Image height="60px" src={content} />
            </div>
          ),
        }[type];
      };

      const time = dayjs(createTime).format('YYYY-MM-DD HH:mm:ss');

      const title = (
        <Title>
          {contentRender(first)}
          <span>{time}</span>
        </Title>
      );

      const more = rest.map(contentRender);

      return (
        <>
          {title}
          {more}
        </>
      );
    } catch (err) {
      return null;
    }
  });

  const content = <Content>{contentList}</Content>;

  const onTranslate = () => {
    setTranslating(true);

    overseaTranslate(textMap['zh-CN'], systemLanguage, 'feedback')
      .then((res) => {
        setTextMap((map) => {
          return {
            ...map,
            [systemLanguage]: res,
          };
        });

        setTranslating(false);

        setTranslateFailed(false);
      })
      .catch(() => {
        setTranslating(false);

        setTranslateFailed(true);
      });
  };

  const translateButton =
    systemLanguage === 'zh-CN'
      ? null
      : (() => {
          if (!shouldTranslate) {
            return (
              <Button type="link" onClick={() => setShouldTranslate(true)}>
                {t('trade.翻译')}
              </Button>
            );
          }

          if (translating) {
            return (
              <Button type="link" loading>
                {t('trade.translatingMsg')}
              </Button>
            );
          }

          if (translateFailed) {
            return (
              <Button type="link" onClick={onTranslate}>
                {t('trade.翻译出错，点击重翻')}
              </Button>
            );
          }

          return (
            <Button type="link" onClick={() => setShouldTranslate(false)}>
              {t('trade.查看原文')}
            </Button>
          );
        })();

  const refsCurrent = {
    onTranslate,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    setTranslateFailed(false);

    if (!shouldTranslate || currentLanguageTextList) {
      return;
    }

    refs.current.onTranslate();
  }, [shouldTranslate, systemLanguage, currentLanguageTextList]);

  if (!render) {
    return content;
  }

  return render(content, translateButton);
}

function DialogueReviewedView(props: { source: Feedback.DialogueSource; message: string }) {
  const { source, message } = props;
  const getAllSticker = useGetAllSticker();
  useMount(() => getAllSticker());
  const { t } = useTranslate();
  const buyerMessage = source?.dialogueReplyContext?.question || [];
  const robotMessage = source?.dialogueReplyV2?.dialogueResponse || [];
  const waitMode = !!source?.waitDetail;
  const human = !!source?.h2HDetail;
  const time = dayjs(Number(source?.timestamp)).format('MM月DD日 HH:mm:ss');

  const noReplay = useMemo(() => {
    if (waitMode) {
      return (
        <div className="flex items-center justify-center">
          <div className="text-[var(--fett-text-secondary)] px-[20px] py-[6px] leading-[32px] rounded-[100px] bg-[#e2ebfa] text-center">
            {t('feedback.waitMode')}
          </div>
        </div>
      );
    }
    if (human) {
      return (
        <div className="flex items-center justify-center">
          <div className="text-[var(--fett-text-secondary)] px-[20px] py-[6px] leading-[20px] rounded-[100px] bg-[#e2ebfa] text-center">
            {t('feedback.humanMode')}
          </div>
        </div>
      );
    }
    return null;
  }, [waitMode, t, human]);

  return (
    <div className="border border-[var(--fett-border-default)] border-solid">
      <div className="h-[40px] px-[16px] py-[9px] text-[16px] text-[var(--fett-text-primary)] leading-[22px] border-0 border-b border-solid border-b-[var(--fett-border-default)]">
        {t('feedback.testResult')}
      </div>
      <div className="bg-[var(--fett-erp-card-bg)] min-h-[60px] py-[16px] px-[14px]">
        <div className="max-w-[500px]">
          {buyerMessage?.map((item) => {
            const { timestamp, query } = item;
            const q = query.replace(/\[(.*?)\](.*)/, function (_match, $1, $2) {
              return `[${t(`messageType.${$1}`)}]`;
            });
            const aq = q.startsWith('http') ? `[${t('messageType.商品卡片')}]` : q;
            const time = dayjs(Number(timestamp)).format('MM月DD日 HH:mm:ss');
            return (
              <div className="mb-[18px]">
                <div className="h-[18px] flex items-center mb-[4px]">
                  <div className="mr-[6px] text-[12px] text-[var(--fett-text-tertiary)]">
                    {t('feedback.buyer')}
                  </div>
                  <div className="text-[10px] text-[var(--fett-text-quaternary)]">{time}</div>
                </div>
                <div className="bg-[var(--fett-bg-panel)] p-[8px] leading-[18px] text-[14px] inline-block">
                  {aq}
                </div>
              </div>
            );
          })}
        </div>

        {noReplay ? (
          noReplay
        ) : (
          <div className="flex justify-end">
            {robotMessage?.map((item) => {
              const sentences = item?.replyResponse?.sentence || [];
              return (
                <div className="mb-[18px] max-w-[500px] flex flex-col items-end">
                  <div className="h-[18px] flex items-center mb-[4px]">
                    <div className="text-[12px] mr-[6px] text-[var(--fett-text-tertiary)]">
                      {t('feedback.lerobot')}
                    </div>
                    <div className="text-[10px] text-[var(--fett-text-quaternary)]">{time}</div>
                  </div>
                  {sentences?.map((each) => {
                    if (each?.text) {
                      return (
                        <div className="mb-[12px] leading-[18px] text-[14px] p-[8px] bg-[#e3f0ff] inline-block">
                          {each?.text?.content}
                        </div>
                      );
                    }
                    if (each?.stickerSentence) {
                      let region, platform;
                      try {
                        const msg = JSON.parse(message);
                        region = msg.region;
                        platform = msg.channel;
                      } catch (e) {}
                      return (
                        <OperateLogSticker
                          channel={platform}
                          region={region}
                          sid={each?.stickerSentence?.stickerId}
                          pid={each?.stickerSentence?.stickerPackageId}
                        />
                      );
                    }
                    if (each?.image) {
                      return (
                        <div className="mb-[12px]">
                          <Image height="60px" src={each?.image?.imageUrl} />
                        </div>
                      );
                    }
                    if (each?.video) {
                      return (
                        <div className="mb-[12px] w-[60px] h-[60px] flex items-center justify-center">
                          <PreviewModalProvider>
                            <SiderImageSlide
                              videos={[each?.video?.localVideoUrl]}
                              count={1}
                              index={0}
                              width={580}
                              offsetPercent={0}
                            />
                          </PreviewModalProvider>
                        </div>
                      );
                    }
                    if (each?.media) {
                      if (each?.media?.mediaType === 'MEDIA_IMAGE') {
                        return (
                          <div className="mb-[12px]">
                            <Image height="60px" src={each?.media?.localUrl} />;
                          </div>
                        );
                      }
                      if (each?.media?.mediaType === 'MEDIA_VIDEO') {
                        return (
                          <div className="mb-[12px] w-[60px] h-[60px] flex items-center justify-center">
                            <PreviewModalProvider>
                              <SiderImageSlide
                                videos={[each?.media?.localUrl]}
                                count={1}
                                index={0}
                                width={580}
                                offsetPercent={0}
                              />
                            </PreviewModalProvider>
                          </div>
                        );
                      }
                      return null;
                    }
                    return null;
                  })}
                </div>
              );
            })}
          </div>
        )}

        <ScenarioName policyDetail={source?.policyDetail} />
      </div>
    </div>
  );
}

function ScenarioName(props: { policyDetail: any }) {
  const { policyDetail } = props;
  const systemLanguage = useSystemLanguageValue();
  const scenarioName = useMemo(() => {
    return policyDetail?.translateData?.find((e: any) => {
      return e?.lang === (systemLanguage === 'zh-CN' ? 'zh_CN' : 'en');
    })?.scenarioName;
  }, [policyDetail, systemLanguage]);
  if (!policyDetail?.scenarioId) {
    return null;
  }
  return scenarioName ? (
    <div className="h-[24px] flex justify-end mt-[-12px]">
      <div className="text-[var(--fett-scenario-accent-text)] bg-[#e3f0ff] px-[8px] leading-[24px]">
        {scenarioName}
      </div>
    </div>
  ) : null;
}

export default OperateLog;
